import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminFetch } from "../../../lib/adminApi";
import { useBlogStaging } from "./useBlogStaging";

function useLoadPostEffect(
  paramSlug: string | undefined,
  blogs: any[],
  navigate: any,
  setters: any,
  setActiveDraft: (d: any) => void
) {
  useEffect(() => {
    if (paramSlug) {
      const post = blogs.find((b) => b.slug === paramSlug);
      if (post) {
        setters.setSlug(post.slug);
        setters.setDate(post.date || new Date().toISOString().split("T")[0]);
        setters.setTitleEn(post.title_en || "");
        setters.setTitleTr(post.title_tr || "");
        setters.setSummaryEn(post.description_en || "");
        setters.setSummaryTr(post.description_tr || "");
        setters.setContentEn(post.content_en || "");
        setters.setContentTr(post.content_tr || "");
        setters.setPublished(post.published || 0);
        setters.setSelectedCategories(post.categories ? post.categories.split(",").map((c: string) => c.trim()).filter(Boolean) : []);
        setters.setSelectedTags(post.tags_en ? post.tags_en.split(",").map((t: string) => t.trim()).filter(Boolean) : []);

        adminFetch(`/api/admin/drafts?item_type=blog&item_id=${paramSlug}`)
          .then((res) => {
            if (res?.success && res.draft) {
              setActiveDraft(res.draft);
              const draft = res.draft;
              setters.setDate(draft.date || "");
              setters.setTitleEn(draft.title_en || "");
              setters.setTitleTr(draft.title_tr || "");
              setters.setSummaryEn(draft.description_en || "");
              setters.setSummaryTr(draft.description_tr || "");
              setters.setContentEn(draft.content_en || "");
              setters.setContentTr(draft.content_tr || "");
              setters.setSelectedCategories(draft.categories ? draft.categories.split(",").map((c: string) => c.trim()).filter(Boolean) : []);
              setters.setSelectedTags(draft.tags_en ? draft.tags_en.split(",").map((t: string) => t.trim()).filter(Boolean) : []);
              toast.info("Unpublished staged draft loaded for editing.");
            }
          })
          .catch((err) => console.error("Failed to load draft:", err));
      } else {
        toast.error("Blog post not found.");
        navigate("/blog");
      }
    }
  }, [paramSlug, blogs, navigate]);
}

function useSaveShortcutEffect(onSave: () => void) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        onSave();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onSave]);
}

interface BlogActionsProps {
  slug: string;
  titleEn: string;
  titleTr: string;
  date: string;
  summaryEn: string;
  summaryTr: string;
  selectedTags: string[];
  contentEn: string;
  contentTr: string;
  published: number;
  selectedCategories: string[];
  setPublished: (p: number) => void;
  setSaving: (s: boolean) => void;
  setPublishing: (p: boolean) => void;
  setActiveDraft: (d: any) => void;
  isEditMode: boolean;
  navigate: any;
  onRefresh: () => void;
  saveDraft: any;
  discardDraft: any;
  blogs: any[];
  paramSlug: string | undefined;
  setDate: (d: string) => void;
  setTitleEn: (t: string) => void;
  setTitleTr: (t: string) => void;
  setSummaryEn: (s: string) => void;
  setSummaryTr: (s: string) => void;
  setContentEn: (c: string) => void;
  setContentTr: (c: string) => void;
  setSelectedCategories: (c: string[]) => void;
  setSelectedTags: (t: string[]) => void;
  activeDraft: any;
}

function validateBlogForm(slug: string, titleEn: string, summaryEn: string, summaryTr: string, isPublish: boolean = false) {
  if (!slug.trim() || !titleEn.trim()) {
    toast.error(isPublish ? "Slug and Title are required before publishing!" : "Slug and English Title are required!");
    return false;
  }
  if (summaryEn.length > 200 || summaryTr.length > 200) {
    toast.error("Summary cannot exceed 200 characters!");
    return false;
  }
  return true;
}

export function useBlogActions({
  slug, titleEn, titleTr, date, summaryEn, summaryTr, selectedTags, contentEn, contentTr, published, selectedCategories,
  setPublished, setSaving, setPublishing, setActiveDraft, isEditMode, navigate, onRefresh, saveDraft, discardDraft, blogs, paramSlug,
  setDate, setTitleEn, setTitleTr, setSummaryEn, setSummaryTr, setContentEn, setContentTr, setSelectedCategories, setSelectedTags,
  activeDraft
}: BlogActionsProps) {
  const getPayload = () => ({
    slug,
    title_en: titleEn,
    title_tr: titleTr,
    date,
    description_en: summaryEn,
    description_tr: summaryTr,
    tags_en: selectedTags.join(","),
    tags_tr: selectedTags.join(","),
    content_en: contentEn,
    content_tr: contentTr,
    published,
    categories: selectedCategories.join(","),
  });

  const handleSave = async () => {
    if (!validateBlogForm(slug, titleEn, summaryEn, summaryTr)) return;
    setSaving(true);
    try {
      const payload = getPayload();
      await adminFetch("/api/admin/drafts", {
        operation: "promote",
        item_type: "blog",
        item_id: slug,
        draft_data: payload
      });
      toast.success("Blog post published live successfully!");
      setActiveDraft(null);
      onRefresh();
      if (!isEditMode) navigate(`/blog/editor/${slug}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to save blog post");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!validateBlogForm(slug, titleEn, summaryEn, summaryTr)) return;
    setSaving(true);
    const success = await saveDraft(slug, getPayload());
    setSaving(false);
    if (success && !isEditMode) navigate(`/blog/editor/${slug}`);
  };

  const handlePublishToggle = async () => {
    if (!validateBlogForm(slug, titleEn, summaryEn, summaryTr, true)) return;
    setPublishing(true);
    const nextPublished = published === 1 ? 0 : 1;
    try {
      const payload = getPayload();
      payload.published = nextPublished;
      await adminFetch("/api/admin/blog", payload);
      setPublished(nextPublished);
      toast.success(nextPublished === 1 ? "Article published live!" : "Article unpublished to draft.");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to update publish state");
    } finally {
      setPublishing(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!isEditMode || !slug.trim()) return;
    const livePost = blogs.find((b) => b.slug === paramSlug);
    await discardDraft(slug, livePost, (post: any) => {
      setDate(post.date || new Date().toISOString().split("T")[0]);
      setTitleEn(post.title_en || "");
      setTitleTr(post.title_tr || "");
      setSummaryEn(post.description_en || "");
      setSummaryTr(post.description_tr || "");
      setContentEn(post.content_en || "");
      setContentTr(post.content_tr || "");
      setSelectedCategories(post.categories ? post.categories.split(",").map((c: string) => c.trim()).filter(Boolean) : []);
      setSelectedTags(post.tags_en ? post.tags_en.split(",").map((t: string) => t.trim()).filter(Boolean) : []);
    });
  };

  return { handleSave, handleSaveDraft, handlePublishToggle, handleDiscardDraft, getPayload };
}

export function useBlogEditor(
  blogs: any[],
  categories: any[],
  tags: any[],
  onRefresh: () => void
) {
  const { slug: paramSlug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  const [slug, setSlug] = useState("");
  const [date, setDate] = useState<string>(new Date().toISOString().split("T")[0] || "");
  const [titleEn, setTitleEn] = useState("");
  const [titleTr, setTitleTr] = useState("");
  const [summaryEn, setSummaryEn] = useState("");
  const [summaryTr, setSummaryTr] = useState("");
  const [contentEn, setContentEn] = useState("");
  const [contentTr, setContentTr] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [published, setPublished] = useState(0);

  const [activeLang, setActiveLang] = useState<"en" | "tr">("en");
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const isEditMode = !!paramSlug;
  const isSlugDisabled = isEditMode || blogs.some((b) => b.slug === slug && b.slug !== paramSlug);

  const { activeDraft, setActiveDraft, saveDraft, discardDraft } = useBlogStaging(onRefresh);

  useLoadPostEffect(paramSlug, blogs, navigate, {
    setSlug, setDate, setTitleEn, setTitleTr, setSummaryEn, setSummaryTr,
    setContentEn, setContentTr, setPublished, setSelectedCategories, setSelectedTags
  }, setActiveDraft);

  const { handleSave, handleSaveDraft, handlePublishToggle, handleDiscardDraft, getPayload } = useBlogActions({
    slug, titleEn, titleTr, date, summaryEn, summaryTr, selectedTags, contentEn, contentTr, published, selectedCategories,
    setPublished, setSaving, setPublishing, setActiveDraft, isEditMode, navigate, onRefresh, saveDraft, discardDraft, blogs, paramSlug,
    setDate, setTitleEn, setTitleTr, setSummaryEn, setSummaryTr, setContentEn, setContentTr, setSelectedCategories, setSelectedTags,
    activeDraft
  });

  useSaveShortcutEffect(handleSave);

  const activeContent = activeLang === "en" ? contentEn : contentTr;
  const handleContentChange = (val: string) => {
    if (activeLang === "en") setContentEn(val);
    else setContentTr(val);
  };

  return {
    slug, setSlug, date, setDate,
    titleEn, setTitleEn, titleTr, setTitleTr,
    summaryEn, setSummaryEn, summaryTr, setSummaryTr,
    contentEn, setContentEn, contentTr, setContentTr,
    selectedCategories, setSelectedCategories,
    selectedTags, setSelectedTags,
    published, setPublished,
    activeLang, setActiveLang,
    showPreview, setShowPreview,
    saving, publishing,
    isSlugDisabled, handleSave, handlePublishToggle,
    activeContent, handleContentChange,
    activeDraft, handleSaveDraft, handleDiscardDraft, getPayload
  };
}

export default useBlogEditor;
